import { CollapsibleCard, Icon } from '@teamsnap/snap-ui';
import { TruncateWithPopover } from 'components/common/truncate/TruncateWithPopover';
import {
  usePostRegEditActions,
  usePostRegEditDrawer,
} from 'hooks/postRegEditDrawer';
import * as React from 'react';
import { EMPTY_SELECT_VALUE, QuestionType } from '../types';

interface InformationChangeItem {
  id: string;
  label: string;
  originalValue: string;
  currentValue: string;
  type: QuestionType;
}

export function InformationChange() {
  const { dirtyFields } = usePostRegEditDrawer();
  const { getOriginalAndCurrentValues } = usePostRegEditActions();
  const items: InformationChangeItem[] = dirtyFields.map((questionId) => {
    const { originalValue, currentValue, question } =
      getOriginalAndCurrentValues(questionId);
    return question
      ? {
          id: questionId,
          label: question.label,
          originalValue,
          currentValue,
          type: question.type,
        }
      : null;
  });
  const infoItems: InformationChangeItem[] = items.filter(
    (item) => item && item.type !== QuestionType.FILE
  );
  const fileItems: InformationChangeItem[] = items.filter(
    (item) => item && item.type === QuestionType.FILE
  );

  function FileChangeTableRow({
    item,
  }: {
    item: InformationChangeItem;
  }): JSX.Element {
    const { label, originalValue, currentValue } = item;

    let fileStatus = '';
    if (!currentValue) {
      fileStatus = 'deleted';
    } else if (!originalValue && !!currentValue) {
      fileStatus = 'uploaded';
    } else {
      fileStatus = 'changed';
    }
    return (
      <tr data-testid="ChangeRow" className="sui-mb-1">
        <td>
          {label}
          {fileStatus === 'deleted' && (
            <span className="sui-text-red sui-ml-1">(deleted)</span>
          )}
          {fileStatus === 'uploaded' && (
            <span className="sui-ml-1">(newly uploaded)</span>
          )}
          {fileStatus === 'changed' && (
            <span className="sui-ml-1">(re-uploaded)</span>
          )}
        </td>
      </tr>
    );
  }

  function ChangeTableRow({
    item,
    showArrow,
  }: {
    item: InformationChangeItem;
    showArrow: boolean;
  }): JSX.Element {
    const { label, originalValue, currentValue } = item;

    return (
      <tr data-testid="ChangeRow" className="sui-mb-1">
        <td>
          <TruncateWithPopover
            text={label}
            maxLength={30}
            style={{ maxWidth: '200px' }}
          />
        </td>
        <td>
          <TruncateWithPopover
            text={originalValue || EMPTY_SELECT_VALUE}
            maxLength={16}
          />
        </td>
        <td>
          {showArrow && (
            <Icon
              data-testid="InformationChange-Arrow"
              style={{ verticalAlign: 'text-bottom' }}
              name="arrow_forward"
            />
          )}
        </td>
        <td>
          <TruncateWithPopover
            text={currentValue || EMPTY_SELECT_VALUE}
            maxLength={16}
          />
        </td>
      </tr>
    );
  }

  return (
    <CollapsibleCard.Root isDefaultOpen>
      <CollapsibleCard.Header title="Information Change" />
      <CollapsibleCard.Content>
        <div>
          <ul className="sui-body sui-text-gray-60 sui-mb-2 sui-ml-2">
            <li>
              Associated fields on the season roster profile will be updated to
              reflect the changes.
            </li>
            <li>
              Edits will <i className="sui-italic">not</i> assign players to any
              team or division.
            </li>
          </ul>
          {infoItems?.length > 0 && (
            <table
              className="sui-w-11/12"
              style={{ borderCollapse: 'separate', borderSpacing: '0.25em' }}
            >
              <thead>
                <tr className="sui-text-left sui-font-semibold">
                  <th className="sui-w-5/12">Question</th>
                  <th className="sui-w-3/12">Original answer</th>
                  {/* Add a space for the arrow for the first row */}
                  <th className="sui-w-1/12"></th>
                  <th className="sui-w-3/12">New answer</th>
                </tr>
              </thead>
              <tbody data-testid="InformationChange-table-body">
                {infoItems.map((item, index) => (
                  <ChangeTableRow
                    key={`${item.id}-${index}`}
                    item={item}
                    showArrow={index === 0}
                  />
                ))}
              </tbody>
            </table>
          )}
          {fileItems?.length > 0 && (
            <table
              className="sui-w-11/12 sui-mt-2"
              style={{ borderCollapse: 'separate', borderSpacing: '0.25em' }}
              data-testid="InformationChange-table-files"
            >
              <thead>
                <tr className="sui-text-left sui-font-semibold">
                  <th className="sui-w-full">Updated files</th>
                </tr>
              </thead>
              <tbody data-testid="InformationChange-table-body-files">
                {fileItems.map((item, index) => (
                  <FileChangeTableRow key={`${item.id}-${index}`} item={item} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CollapsibleCard.Content>
    </CollapsibleCard.Root>
  );
}
